import type {
  DatasetResource,
  DestinationResource,
  MappingOptions,
  MappingResource,
  SourceResource,
  StreamOptions,
  TestStreamTransformResponse,
  UpdateStreamOptions,
} from "@prequel/import-react";

import type { ProductResource } from "@/api/import/products";
import type { ProviderResource } from "@/api/import/providers";

export type DestinationField = {
  name: string;
  type: string;
  required: boolean;
  schema: unknown;
};

type JsonObject = Record<string, unknown>;

export const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export type StreamMappingDraft = {
  fieldType: "field" | "expression";
  columnName: string;
  expression: string;
};

export const createEmptyMappingDraft = (): StreamMappingDraft => ({
  fieldType: "field",
  columnName: "",
  expression: "",
});

export const isMappingDraftConfigured = (mapping: StreamMappingDraft | undefined): boolean => {
  if (!mapping) {
    return false;
  }

  return mapping.fieldType === "expression" ? mapping.expression.trim().length > 0 : mapping.columnName.length > 0;
};

export const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const formatSchemaType = (schema: unknown): string => {
  if (!isJsonObject(schema)) {
    return "unknown";
  }

  const type = schema.type;
  if (Array.isArray(type)) {
    return type.filter((item): item is string => typeof item === "string").join(" | ") || "unknown";
  }

  if (typeof type === "string") {
    return type;
  }

  return "unknown";
};

export const getDestinationFields = (recordSchema: unknown): DestinationField[] => {
  const schema = parseMaybeJson(recordSchema);
  if (!isJsonObject(schema) || !isJsonObject(schema.properties)) {
    return [];
  }

  const requiredFields = Array.isArray(schema.required)
    ? new Set(schema.required.filter((field): field is string => typeof field === "string"))
    : new Set<string>();

  return Object.entries(schema.properties).map(([name, property]) => ({
    name,
    type: formatSchemaType(property),
    required: requiredFields.has(name),
    schema: property,
  }));
};

export const getProviderDatasets = (
  providerID: string,
  datasets: DatasetResource[],
  sources: SourceResource[],
): DatasetResource[] => {
  const providerSourceIDs = new Set(
    sources.filter((source) => source.provider_id === providerID).map((source) => source.id),
  );

  return datasets.filter((dataset) => providerSourceIDs.has(dataset.source_id));
};

export const getProviderDestinations = (
  provider: ProviderResource | undefined,
  products: ProductResource[],
  destinations: DestinationResource[],
): DestinationResource[] => {
  if (!provider) {
    return [];
  }

  const providerProductIDs = new Set(provider.products ?? []);
  if (providerProductIDs.has("all")) {
    return destinations;
  }

  const destinationIDs = new Set(
    products
      .filter((product) => providerProductIDs.has(product.id) || providerProductIDs.has(product.name))
      .flatMap((product) => product.destinations ?? []),
  );

  return destinations.filter(
    (destination) => destinationIDs.has(destination.id) || destinationIDs.has(destination.name),
  );
};

export const getDatasetDisplayName = (dataset: DatasetResource): string => {
  if (dataset.method === "table" && dataset.table) {
    return `${dataset.table.source_table_name} (${dataset.id})`;
  }

  return `${dataset.id} (${dataset.type})`;
};

export const getSampleRecordIDs = (records: unknown[] | undefined | null): string[] =>
  (records ?? [])
    .map((record) => (isJsonObject(record) && typeof record.record_id === "string" ? record.record_id : undefined))
    .filter((recordID): recordID is string => Boolean(recordID));

export const getFirstTransformError = (response: TestStreamTransformResponse): string | undefined => {
  const erroredRecord = (response.records ?? []).find(
    (record) => isJsonObject(record) && typeof record.error_code === "string",
  );
  if (!isJsonObject(erroredRecord)) {
    return undefined;
  }

  const code = typeof erroredRecord.error_code === "string" ? erroredRecord.error_code : "transform_error";
  const trace = typeof erroredRecord.error_trace === "string" ? erroredRecord.error_trace : undefined;
  return trace ? `${code}: ${trace}` : code;
};

const parseTransformedData = (record: unknown): unknown => {
  if (!isJsonObject(record)) {
    return undefined;
  }

  return parseMaybeJson(record.transformed_data);
};

export const getTransformedDataPreview = (
  response: TestStreamTransformResponse,
  fields: DestinationField[],
): TestStreamTransformResponse => ({
  schema: fields.map((field) => ({
    name: field.name,
    database_type_name: field.type,
  })),
  // Keep one row per input record (errored records parse to null) so the preview stays aligned with the error report.
  records: (response.records ?? []).map(parseTransformedData),
});

const toMappingOptions = (mappingsByTarget: Record<string, StreamMappingDraft>): MappingOptions[] =>
  Object.entries(mappingsByTarget).reduce<MappingOptions[]>((acc, [targetField, mapping]) => {
    if (!isMappingDraftConfigured(mapping)) {
      return acc;
    }

    if (mapping.fieldType === "expression") {
      acc.push({
        field_type: "expression",
        expression: { expr: mapping.expression.trim() },
        target_field: targetField,
      });
    } else {
      acc.push({
        field_type: "field",
        column: { name: mapping.columnName },
        target_field: targetField,
      });
    }

    return acc;
  }, []);

export const buildStreamOptions = ({
  name,
  datasetID,
  destinationID,
  mappingsByTarget,
}: {
  name: string;
  datasetID: string;
  destinationID: string;
  mappingsByTarget: Record<string, StreamMappingDraft>;
}): StreamOptions => ({
  name: name.trim(),
  is_enabled: false,
  dataset_id: datasetID,
  destination_id: destinationID,
  mappings: toMappingOptions(mappingsByTarget),
});

// The dataset and destination are fixed once a stream exists, so an update only carries the editable fields.
export const buildUpdateStreamOptions = ({
  name,
  isEnabled,
  mappingsByTarget,
}: {
  name?: string;
  isEnabled?: boolean;
  mappingsByTarget: Record<string, StreamMappingDraft>;
}): UpdateStreamOptions => ({
  ...(name === undefined ? {} : { name: name.trim() }),
  ...(isEnabled === undefined ? {} : { is_enabled: isEnabled }),
  mappings: toMappingOptions(mappingsByTarget),
});

export const mappingsToDrafts = (mappings: MappingResource[] | undefined): Record<string, StreamMappingDraft> =>
  (mappings ?? []).reduce<Record<string, StreamMappingDraft>>((acc, mapping) => {
    if (!mapping.target_field) {
      return acc;
    }

    acc[mapping.target_field] =
      mapping.field_type === "expression"
        ? { ...createEmptyMappingDraft(), fieldType: "expression", expression: mapping.expression?.expr ?? "" }
        : { ...createEmptyMappingDraft(), fieldType: "field", columnName: mapping.column?.name ?? "" };

    return acc;
  }, {});

export const getDatasetProviderID = (datasetID: string, datasets: DatasetResource[]): string =>
  datasets.find((dataset) => dataset.id === datasetID)?.provider_id ?? "";
